import type { Action } from '@0xtorch/core'
import type { BlockEvent } from '../../types'
import { createBalancesDepositTransfer } from './createTransfer'
import { balancesDepositSchema, dappStakingRewardSchema } from './schemas'
import type { HandleEventParameters, HandleEventReturnType } from './types'

/** dappStaking.Reward に対応する balances.Deposit を staking-reward action へ変換する。 */
export const convertDappStakingRewards = ({
  chain,
  timestamp,
  source,
  events,
  relatedAddresses,
  debug,
}: HandleEventParameters): HandleEventReturnType => {
  const rewardAmountsByDepositIndex = new Map<number, bigint>()
  for (const [depositIndex, event] of events.entries()) {
    if (event.section !== 'balances' || event.method !== 'Deposit') {
      continue
    }
    const parsedDeposit = balancesDepositSchema.safeParse(event.data)
    if (
      !parsedDeposit.success ||
      !relatedAddresses.has(parsedDeposit.data.who)
    ) {
      continue
    }

    let rewardAmount = 0n
    for (
      let rewardIndex = depositIndex + 1;
      rewardIndex < events.length;
      rewardIndex += 1
    ) {
      const rewardEvent = events[rewardIndex]
      if (
        rewardEvent === undefined ||
        rewardEvent.section !== 'dappStaking' ||
        rewardEvent.method !== 'Reward'
      ) {
        break
      }
      const parsedReward = dappStakingRewardSchema.safeParse(rewardEvent.data)
      if (
        !parsedReward.success ||
        parsedReward.data.account !== parsedDeposit.data.who
      ) {
        break
      }
      rewardAmount += parsedReward.data.amount
    }

    if (rewardAmount > 0n && parsedDeposit.data.amount >= rewardAmount) {
      rewardAmountsByDepositIndex.set(depositIndex, rewardAmount)
    }
  }
  if (rewardAmountsByDepositIndex.size === 0) {
    return undefined
  }

  const actions: Action[] = []
  const unhandledEvents: BlockEvent[] = []
  for (const [eventIndex, event] of events.entries()) {
    const rewardAmount = rewardAmountsByDepositIndex.get(eventIndex)
    if (rewardAmount === undefined) {
      unhandledEvents.push(event)
      continue
    }

    const parsed = balancesDepositSchema.safeParse(event.data)
    if (!parsed.success) {
      return undefined
    }
    const transfer = createBalancesDepositTransfer({
      chain,
      data: { who: parsed.data.who, amount: rewardAmount.toString() },
      relatedAddresses,
      debug,
    })
    if (transfer === undefined) {
      return undefined
    }
    actions.push({
      type: 'staking-reward',
      timestamp,
      source,
      order: 0,
      evidence: 'contract',
      transfers: [transfer],
    })

    const unhandledAmount = parsed.data.amount - rewardAmount
    if (unhandledAmount > 0n) {
      unhandledEvents.push({
        ...event,
        data: { who: parsed.data.who, amount: unhandledAmount.toString() },
      })
    }
  }

  return { actions, unhandledEvents }
}
